import { colorGroups as chartColorGroups } from '@unifyapps/defs/blocks/Chart/chartColorGroups';
import { backgroundColorGroups } from '@unifyapps/defs/constants/backgroundColorGroups';
import { ApplicationType } from '@unifyapps/defs/types/interface';
import type { TFunction } from '@unifyapps/i18n/client';
import { isNative } from '@unifyapps/ui/utils/isNative';
import type { ColorGroups } from '../../../../components/ColorPicker';
import { isUrl } from '../../../../utils/isUrl';
import { borderColorGroups, outlineColorGroups } from './color';
import type { AppearanceStyle, StyleSizeOption } from './types';

export const getGroupNameFromGroupId = ({ groupId, t }: { groupId: string; t: TFunction }) => {
  switch (groupId) {
    case 'NEGATIVE':
      return t('interfaces:Styles.NegativeValues');
    default:
      return groupId;
  }
};

const getMarginConfig = (i18nextT: TFunction, applicationType: ApplicationType | undefined) => ({
  isDirectional: true,
  customStyles: {
    customDirectionKeys: ['t', 'r', 'b', 'l'],
    enabled: true,
  },
  options: {
    all: [
      { id: 'm-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'm-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'm-xs', name: 'xs', styleValue: '4px' },
      { id: 'm-sm', name: 'sm', styleValue: '6px' },
      { id: 'm-md', name: 'md', styleValue: '8px' },
      { id: 'm-lg', name: 'lg', styleValue: '12px' },
      { id: 'm-xl', name: 'xl', styleValue: '16px' },
      { id: 'm-2xl', name: '2xl', styleValue: '20px' },
      { id: 'm-3xl', name: '3xl', styleValue: '24px' },
      { id: 'm-4xl', name: '4xl', styleValue: '32px' },
      { id: 'm-5xl', name: '5xl', styleValue: '40px' },
      { id: 'm-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'm-[84px]', name: '84px', styleValue: '84px' },
      { id: 'm-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'm-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'm-auto', name: 'Auto' },
      { id: '-m-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-m-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-m-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-m-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-m-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-m-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-m-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-m-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-m-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-m-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-m-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'm-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-m-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
    x: [
      { id: 'mx-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'mx-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'mx-xs', name: 'xs', styleValue: '4px' },
      { id: 'mx-sm', name: 'sm', styleValue: '6px' },
      { id: 'mx-md', name: 'md', styleValue: '8px' },
      { id: 'mx-lg', name: 'lg', styleValue: '12px' },
      { id: 'mx-xl', name: 'xl', styleValue: '16px' },
      { id: 'mx-2xl', name: '2xl', styleValue: '20px' },
      { id: 'mx-3xl', name: '3xl', styleValue: '24px' },
      { id: 'mx-4xl', name: '4xl', styleValue: '32px' },
      { id: 'mx-5xl', name: '5xl', styleValue: '40px' },
      { id: 'mx-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'mx-[84px]', name: '84px', styleValue: '84px' },
      { id: 'mx-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'mx-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'mx-auto', name: 'Auto' },
      { id: '-mx-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-mx-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-mx-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-mx-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-mx-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-mx-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-mx-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-mx-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-mx-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-mx-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: 'mx-[-48px]', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'mx-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-mx-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
    y: [
      { id: 'my-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'my-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'my-xs', name: 'xs', styleValue: '4px' },
      { id: 'my-sm', name: 'sm', styleValue: '6px' },
      { id: 'my-md', name: 'md', styleValue: '8px' },
      { id: 'my-lg', name: 'lg', styleValue: '12px' },
      { id: 'my-xl', name: 'xl', styleValue: '16px' },
      { id: 'my-2xl', name: '2xl', styleValue: '20px' },
      { id: 'my-3xl', name: '3xl', styleValue: '24px' },
      { id: 'my-4xl', name: '4xl', styleValue: '32px' },
      { id: 'my-5xl', name: '5xl', styleValue: '40px' },
      { id: 'my-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'my-[84px]', name: '84px', styleValue: '84px' },
      { id: 'my-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'my-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'my-auto', name: 'Auto' },
      { id: '-my-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-my-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-my-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-my-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-my-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-my-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-my-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-my-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-my-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-my-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: 'my-[-48px]', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'my-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-my-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
    t: [
      { id: 'mt-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'mt-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'mt-xs', name: 'xs', styleValue: '4px' },
      { id: 'mt-sm', name: 'sm', styleValue: '6px' },
      { id: 'mt-md', name: 'md', styleValue: '8px' },
      { id: 'mt-lg', name: 'lg', styleValue: '12px' },
      { id: 'mt-xl', name: 'xl', styleValue: '16px' },
      { id: 'mt-2xl', name: '2xl', styleValue: '20px' },
      { id: 'mt-3xl', name: '3xl', styleValue: '24px' },
      { id: 'mt-4xl', name: '4xl', styleValue: '32px' },
      { id: 'mt-5xl', name: '5xl', styleValue: '40px' },
      { id: 'mt-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'mt-[84px]', name: '84px', styleValue: '84px' },
      { id: 'mt-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'mt-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'mt-auto', name: 'Auto' },
      { id: '-mt-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-mt-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-mt-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-mt-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-mt-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-mt-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-mt-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-mt-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-mt-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-mt-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: 'mt-[-48px]', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'mt-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-mt-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
    r: [
      { id: 'me-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'me-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'me-xs', name: 'xs', styleValue: '4px' },
      { id: 'me-sm', name: 'sm', styleValue: '6px' },
      { id: 'me-md', name: 'md', styleValue: '8px' },
      { id: 'me-lg', name: 'lg', styleValue: '12px' },
      { id: 'me-xl', name: 'xl', styleValue: '16px' },
      { id: 'me-2xl', name: '2xl', styleValue: '20px' },
      { id: 'me-3xl', name: '3xl', styleValue: '24px' },
      { id: 'me-4xl', name: '4xl', styleValue: '32px' },
      { id: 'me-5xl', name: '5xl', styleValue: '40px' },
      { id: 'me-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'me-[84px]', name: '84px', styleValue: '84px' },
      { id: 'me-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'me-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'me-auto', name: 'Auto' },
      { id: '-me-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-me-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-me-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-me-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-me-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-me-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-me-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-me-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-me-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-me-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: 'me-[-48px]', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'me-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-me-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
    b: [
      { id: 'mb-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'mb-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'mb-xs', name: 'xs', styleValue: '4px' },
      { id: 'mb-sm', name: 'sm', styleValue: '6px' },
      { id: 'mb-md', name: 'md', styleValue: '8px' },
      { id: 'mb-lg', name: 'lg', styleValue: '12px' },
      { id: 'mb-xl', name: 'xl', styleValue: '16px' },
      { id: 'mb-2xl', name: '2xl', styleValue: '20px' },
      { id: 'mb-3xl', name: '3xl', styleValue: '24px' },
      { id: 'mb-4xl', name: '4xl', styleValue: '32px' },
      { id: 'mb-5xl', name: '5xl', styleValue: '40px' },
      { id: 'mb-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'mb-[84px]', name: '84px', styleValue: '84px' },
      { id: 'mb-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'mb-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'mb-auto', name: 'Auto' },
      { id: '-mb-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-mb-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-mb-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-mb-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-mb-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-mb-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-mb-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-mb-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-mb-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-mb-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: 'mb-[-48px]', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'mb-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-mb-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
    l: [
      { id: 'ms-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'ms-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'ms-xs', name: 'xs', styleValue: '4px' },
      { id: 'ms-sm', name: 'sm', styleValue: '6px' },
      { id: 'ms-md', name: 'md', styleValue: '8px' },
      { id: 'ms-lg', name: 'lg', styleValue: '12px' },
      { id: 'ms-xl', name: 'xl', styleValue: '16px' },
      { id: 'ms-2xl', name: '2xl', styleValue: '20px' },
      { id: 'ms-3xl', name: '3xl', styleValue: '24px' },
      { id: 'ms-4xl', name: '4xl', styleValue: '32px' },
      { id: 'ms-5xl', name: '5xl', styleValue: '40px' },
      { id: 'ms-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'ms-[84px]', name: '84px', styleValue: '84px' },
      { id: 'ms-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'ms-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'ms-auto', name: 'Auto' },
      { id: '-ms-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-ms-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-ms-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-ms-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-ms-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-ms-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-ms-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-ms-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-ms-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-ms-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: 'ms-[-48px]', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'ms-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-ms-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
  },
});

const getPaddingConfig = (i18nextT: TFunction, applicationType: ApplicationType | undefined) => ({
  isDirectional: true,
  customStyles: {
    customDirectionKeys: ['t', 'r', 'b', 'l'],
    enabled: true,
  },
  options: {
    all: [
      { id: 'p-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'p-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'p-xs', name: 'xs', styleValue: '4px' },
      { id: 'p-sm', name: 'sm', styleValue: '6px' },
      { id: 'p-md', name: 'md', styleValue: '8px' },
      { id: 'p-lg', name: 'lg', styleValue: '12px' },
      { id: 'p-xl', name: 'xl', styleValue: '16px' },
      { id: 'p-2xl', name: '2xl', styleValue: '20px' },
      { id: 'p-3xl', name: '3xl', styleValue: '24px' },
      { id: 'p-4xl', name: '4xl', styleValue: '32px' },
      { id: 'p-5xl', name: '5xl', styleValue: '40px' },
      { id: 'p-6xl', name: '6xl', styleValue: '48px' },
      { id: 'p-7xl', name: '7xl', styleValue: '64px' },
      { id: 'p-8xl', name: '8xl', styleValue: '80px' },
      { id: 'p-9xl', name: '9xl', styleValue: '96px' },
      { id: 'p-10xl', name: '10xl', styleValue: '128px' },
      { id: 'p-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'p-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-p-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-p-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-p-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-p-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-p-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-p-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-p-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-p-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-p-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-p-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-p-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-p-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-p-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-p-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-p-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-p-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
    x: [
      { id: 'px-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'px-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'px-xs', name: 'xs', styleValue: '4px' },
      { id: 'px-sm', name: 'sm', styleValue: '6px' },
      { id: 'px-md', name: 'md', styleValue: '8px' },
      { id: 'px-lg', name: 'lg', styleValue: '12px' },
      { id: 'px-xl', name: 'xl', styleValue: '16px' },
      { id: 'px-2xl', name: '2xl', styleValue: '20px' },
      { id: 'px-3xl', name: '3xl', styleValue: '24px' },
      { id: 'px-4xl', name: '4xl', styleValue: '32px' },
      { id: 'px-5xl', name: '5xl', styleValue: '40px' },
      { id: 'px-6xl', name: '6xl', styleValue: '48px' },
      { id: 'px-7xl', name: '7xl', styleValue: '64px' },
      { id: 'px-8xl', name: '8xl', styleValue: '80px' },
      { id: 'px-9xl', name: '9xl', styleValue: '96px' },
      { id: 'px-10xl', name: '10xl', styleValue: '128px' },
      { id: 'px-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'px-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-px-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-px-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-px-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-px-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-px-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-px-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-px-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-px-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-px-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-px-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-px-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-px-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-px-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-px-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-px-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-px-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
    y: [
      { id: 'py-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'py-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'py-xs', name: 'xs', styleValue: '4px' },
      { id: 'py-sm', name: 'sm', styleValue: '6px' },
      { id: 'py-md', name: 'md', styleValue: '8px' },
      { id: 'py-lg', name: 'lg', styleValue: '12px' },
      { id: 'py-xl', name: 'xl', styleValue: '16px' },
      { id: 'py-2xl', name: '2xl', styleValue: '20px' },
      { id: 'py-3xl', name: '3xl', styleValue: '24px' },
      { id: 'py-4xl', name: '4xl', styleValue: '32px' },
      { id: 'py-5xl', name: '5xl', styleValue: '40px' },
      { id: 'py-6xl', name: '6xl', styleValue: '48px' },
      { id: 'py-7xl', name: '7xl', styleValue: '64px' },
      { id: 'py-8xl', name: '8xl', styleValue: '80px' },
      { id: 'py-9xl', name: '9xl', styleValue: '96px' },
      { id: 'py-10xl', name: '10xl', styleValue: '128px' },
      { id: 'py-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'py-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-py-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-py-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-py-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-py-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-py-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-py-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-py-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-py-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-py-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-py-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-py-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-py-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-py-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-py-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-py-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-py-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
    t: [
      { id: 'pt-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'pt-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'pt-xs', name: 'xs', styleValue: '4px' },
      { id: 'pt-sm', name: 'sm', styleValue: '6px' },
      { id: 'pt-md', name: 'md', styleValue: '8px' },
      { id: 'pt-lg', name: 'lg', styleValue: '12px' },
      { id: 'pt-xl', name: 'xl', styleValue: '16px' },
      { id: 'pt-2xl', name: '2xl', styleValue: '20px' },
      { id: 'pt-3xl', name: '3xl', styleValue: '24px' },
      { id: 'pt-4xl', name: '4xl', styleValue: '32px' },
      { id: 'pt-5xl', name: '5xl', styleValue: '40px' },
      { id: 'pt-6xl', name: '6xl', styleValue: '48px' },
      { id: 'pt-7xl', name: '7xl', styleValue: '64px' },
      { id: 'pt-8xl', name: '8xl', styleValue: '80px' },
      { id: 'pt-9xl', name: '9xl', styleValue: '96px' },
      { id: 'pt-10xl', name: '10xl', styleValue: '128px' },
      { id: 'pt-[156px]', name: '156px', styleValue: '156px' },
      { id: 'pt-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'pt-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-pt-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-pt-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-pt-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-pt-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-pt-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-pt-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-pt-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-pt-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-pt-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-pt-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-pt-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-pt-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-pt-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-pt-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-pt-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-pt-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
    r: [
      { id: 'pe-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'pe-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'pe-xs', name: 'xs', styleValue: '4px' },
      { id: 'pe-sm', name: 'sm', styleValue: '6px' },
      { id: 'pe-md', name: 'md', styleValue: '8px' },
      { id: 'pe-lg', name: 'lg', styleValue: '12px' },
      { id: 'pe-xl', name: 'xl', styleValue: '16px' },
      { id: 'pe-2xl', name: '2xl', styleValue: '20px' },
      { id: 'pe-3xl', name: '3xl', styleValue: '24px' },
      { id: 'pe-4xl', name: '4xl', styleValue: '32px' },
      { id: 'pe-5xl', name: '5xl', styleValue: '40px' },
      { id: 'pe-6xl', name: '6xl', styleValue: '48px' },
      { id: 'pe-7xl', name: '7xl', styleValue: '64px' },
      { id: 'pe-8xl', name: '8xl', styleValue: '80px' },
      { id: 'pe-9xl', name: '9xl', styleValue: '96px' },
      { id: 'pe-10xl', name: '10xl', styleValue: '128px' },
      { id: 'pe-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'pe-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-pe-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-pe-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-pe-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-pe-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-pe-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-pe-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-pe-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-pe-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-pe-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-pe-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-pe-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-pe-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-pe-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-pe-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-pe-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-pe-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
    b: [
      { id: 'pb-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'pb-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'pb-xs', name: 'xs', styleValue: '4px' },
      { id: 'pb-sm', name: 'sm', styleValue: '6px' },
      { id: 'pb-md', name: 'md', styleValue: '8px' },
      { id: 'pb-lg', name: 'lg', styleValue: '12px' },
      { id: 'pb-xl', name: 'xl', styleValue: '16px' },
      { id: 'pb-2xl', name: '2xl', styleValue: '20px' },
      { id: 'pb-3xl', name: '3xl', styleValue: '24px' },
      { id: 'pb-4xl', name: '4xl', styleValue: '32px' },
      { id: 'pb-5xl', name: '5xl', styleValue: '40px' },
      { id: 'pb-6xl', name: '6xl', styleValue: '48px' },
      { id: 'pb-7xl', name: '7xl', styleValue: '64px' },
      { id: 'pb-8xl', name: '8xl', styleValue: '80px' },
      { id: 'pb-9xl', name: '9xl', styleValue: '96px' },
      { id: 'pb-10xl', name: '10xl', styleValue: '128px' },
      { id: 'pb-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'pb-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-pb-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-pb-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-pb-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-pb-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-pb-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-pb-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-pb-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-pb-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-pb-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-pb-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-pb-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-pb-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-pb-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-pb-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-pb-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-pb-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
    l: [
      { id: 'ps-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'ps-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'ps-xs', name: 'xs', styleValue: '4px' },
      { id: 'ps-sm', name: 'sm', styleValue: '6px' },
      { id: 'ps-md', name: 'md', styleValue: '8px' },
      { id: 'ps-lg', name: 'lg', styleValue: '12px' },
      { id: 'ps-xl', name: 'xl', styleValue: '16px' },
      { id: 'ps-2xl', name: '2xl', styleValue: '20px' },
      { id: 'ps-3xl', name: '3xl', styleValue: '24px' },
      { id: 'ps-4xl', name: '4xl', styleValue: '32px' },
      { id: 'ps-5xl', name: '5xl', styleValue: '40px' },
      { id: 'ps-6xl', name: '6xl', styleValue: '48px' },
      { id: 'ps-7xl', name: '7xl', styleValue: '64px' },
      { id: 'ps-8xl', name: '8xl', styleValue: '80px' },
      { id: 'ps-9xl', name: '9xl', styleValue: '96px' },
      { id: 'ps-10xl', name: '10xl', styleValue: '128px' },
      { id: 'ps-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'ps-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-ps-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-ps-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-ps-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-ps-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-ps-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-ps-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-ps-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-ps-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-ps-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-ps-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-ps-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-ps-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-ps-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-ps-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-ps-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-ps-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
  },
});

const getTableCellPaddingConfig = (i18nextT: TFunction) => ({
  isDirectional: true,
  customStyles: {
    customDirectionKeys: ['t', 'r', 'b', 'l'],
    enabled: true,
  },
  options: {
    all: [
      { id: '0px', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: '2px', name: 'xxs', styleValue: '2px' },
      { id: '4px', name: 'xs', styleValue: '4px' },
      { id: '6px', name: 'sm', styleValue: '6px' },
      { id: '8px', name: 'md', styleValue: '8px' },
      { id: '12px', name: 'lg', styleValue: '12px' },
      { id: '16px', name: 'xl', styleValue: '16px' },
      { id: '20px', name: '2xl', styleValue: '20px' },
    ],
    x: [
      { id: '0px', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: '2px', name: 'xxs', styleValue: '2px' },
      { id: '4px', name: 'xs', styleValue: '4px' },
      { id: '6px', name: 'sm', styleValue: '6px' },
      { id: '8px', name: 'md', styleValue: '8px' },
      { id: '12px', name: 'lg', styleValue: '12px' },
      { id: '16px', name: 'xl', styleValue: '16px' },
      { id: '20px', name: '2xl', styleValue: '20px' },
    ],
    y: [
      { id: '0px', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: '2px', name: 'xxs', styleValue: '2px' },
      { id: '4px', name: 'xs', styleValue: '4px' },
      { id: '6px', name: 'sm', styleValue: '6px' },
      { id: '8px', name: 'md', styleValue: '8px' },
      { id: '12px', name: 'lg', styleValue: '12px' },
      { id: '16px', name: 'xl', styleValue: '16px' },
      { id: '20px', name: '2xl', styleValue: '20px' },
    ],
    t: [
      { id: '0px', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: '2px', name: 'xxs', styleValue: '2px' },
      { id: '4px', name: 'xs', styleValue: '4px' },
      { id: '6px', name: 'sm', styleValue: '6px' },
      { id: '8px', name: 'md', styleValue: '8px' },
      { id: '12px', name: 'lg', styleValue: '12px' },
      { id: '16px', name: 'xl', styleValue: '16px' },
      { id: '20px', name: '2xl', styleValue: '20px' },
    ],
    r: [
      { id: '0px', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: '2px', name: 'xxs', styleValue: '2px' },
      { id: '4px', name: 'xs', styleValue: '4px' },
      { id: '6px', name: 'sm', styleValue: '6px' },
      { id: '8px', name: 'md', styleValue: '8px' },
      { id: '12px', name: 'lg', styleValue: '12px' },
      { id: '16px', name: 'xl', styleValue: '16px' },
      { id: '20px', name: '2xl', styleValue: '20px' },
    ],
    b: [
      { id: '0px', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: '2px', name: 'xxs', styleValue: '2px' },
      { id: '4px', name: 'xs', styleValue: '4px' },
      { id: '6px', name: 'sm', styleValue: '6px' },
      { id: '8px', name: 'md', styleValue: '8px' },
      { id: '12px', name: 'lg', styleValue: '12px' },
      { id: '16px', name: 'xl', styleValue: '16px' },
      { id: '20px', name: '2xl', styleValue: '20px' },
    ],
    l: [
      { id: '0px', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: '2px', name: 'xxs', styleValue: '2px' },
      { id: '4px', name: 'xs', styleValue: '4px' },
      { id: '6px', name: 'sm', styleValue: '6px' },
      { id: '8px', name: 'md', styleValue: '8px' },
      { id: '12px', name: 'lg', styleValue: '12px' },
      { id: '16px', name: 'xl', styleValue: '16px' },
      { id: '20px', name: '2xl', styleValue: '20px' },
    ],
  },
});

// ============================================================================
// ATOMIC STYLE OPTION FUNCTIONS
// Each function returns options for a specific styling category
// ============================================================================

/**
 * Returns gap style options for flex/grid layouts
 */
const getGapOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'gap-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'gap-xxs', name: 'xxs', styleValue: '2px' },
    { id: 'gap-xs', name: 'xs', styleValue: '4px' },
    { id: 'gap-sm', name: 'sm', styleValue: '6px' },
    { id: 'gap-md', name: 'md', styleValue: '8px' },
    { id: 'gap-lg', name: 'lg', styleValue: '12px' },
    { id: 'gap-xl', name: 'xl', styleValue: '16px' },
    { id: 'gap-2xl', name: '2xl', styleValue: '20px' },
    { id: 'gap-3xl', name: '3xl', styleValue: '24px' },
    { id: 'gap-4xl', name: '4xl', styleValue: '32px' },
    { id: 'gap-5xl', name: '5xl', styleValue: '40px' },
  ],
  x: [
    { id: 'gap-x-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'gap-x-xxs', name: 'xxs', styleValue: '2px' },
    { id: 'gap-x-xs', name: 'xs', styleValue: '4px' },
    { id: 'gap-x-sm', name: 'sm', styleValue: '6px' },
    { id: 'gap-x-md', name: 'md', styleValue: '8px' },
    { id: 'gap-x-lg', name: 'lg', styleValue: '12px' },
    { id: 'gap-x-xl', name: 'xl', styleValue: '16px' },
    { id: 'gap-x-2xl', name: '2xl', styleValue: '20px' },
    { id: 'gap-x-3xl', name: '3xl', styleValue: '24px' },
    { id: 'gap-x-4xl', name: '4xl', styleValue: '32px' },
    { id: 'gap-x-5xl', name: '5xl', styleValue: '40px' },
  ],
  y: [
    { id: 'gap-y-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'gap-y-xxs', name: 'xxs', styleValue: '2px' },
    { id: 'gap-y-xs', name: 'xs', styleValue: '4px' },
    { id: 'gap-y-sm', name: 'sm', styleValue: '6px' },
    { id: 'gap-y-md', name: 'md', styleValue: '8px' },
    { id: 'gap-y-lg', name: 'lg', styleValue: '12px' },
    { id: 'gap-y-xl', name: 'xl', styleValue: '16px' },
    { id: 'gap-y-2xl', name: '2xl', styleValue: '20px' },
    { id: 'gap-y-3xl', name: '3xl', styleValue: '24px' },
    { id: 'gap-y-4xl', name: '4xl', styleValue: '32px' },
    { id: 'gap-y-5xl', name: '5xl', styleValue: '40px' },
  ],
});

/**
 * Returns box shadow style options
 */
const getBoxShadowOptions = (i18nextT: TFunction) => ({
  // styleValue mirrors the native styleObject — same source-of-truth value used by
  // the llm-tools CSS plugin for customCSS ↔ appearance.styles round-tripping.
  // hideStyleValueInUI keeps the dropdown label unchanged.
  all: [
    {
      id: 'shadow-none',
      name: i18nextT('interfaces:Styles.None'),
      styleValue: 'none',
      hideStyleValueInUI: true,
      ...(isNative() && { styleObject: { boxShadow: 'none' } }),
    },
    {
      id: 'shadow-xs',
      name: 'xs',
      styleValue: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      hideStyleValueInUI: true,
      ...(isNative() && {
        styleObject: { boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)' },
      }),
    },
    {
      id: 'shadow-sm',
      name: 'sm',
      styleValue: '0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.1)',
      hideStyleValueInUI: true,
      ...(isNative() && {
        styleObject: {
          boxShadow:
            '0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.1)',
        },
      }),
    },
    {
      id: 'shadow-md',
      name: 'md',
      styleValue: '0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1)',
      hideStyleValueInUI: true,
      ...(isNative() && {
        styleObject: {
          boxShadow:
            '0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1)',
        },
      }),
    },
    {
      id: 'shadow-lg',
      name: 'lg',
      styleValue:
        '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
      hideStyleValueInUI: true,
      ...(isNative() && {
        styleObject: {
          boxShadow:
            '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
        },
      }),
    },
    {
      id: 'shadow-xl',
      name: 'xl',
      styleValue:
        '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
      hideStyleValueInUI: true,
      ...(isNative() && {
        styleObject: {
          boxShadow:
            '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
        },
      }),
    },
    {
      id: 'shadow-2xl',
      name: '2xl',
      styleValue: '0px 24px 48px -12px rgba(16, 24, 40, 0.18)',
      hideStyleValueInUI: true,
      ...(isNative() && {
        styleObject: { boxShadow: '0px 24px 48px -12px rgba(16, 24, 40, 0.18)' },
      }),
    },
    {
      id: 'shadow-3xl',
      name: '3xl',
      styleValue: '0px 32px 64px -12px rgba(16, 24, 40, 0.14)',
      hideStyleValueInUI: true,
      ...(isNative() && {
        styleObject: { boxShadow: '0px 32px 64px -12px rgba(16, 24, 40, 0.14)' },
      }),
    },
    {
      id: 'shadow-overflow-left',
      name: 'overflow-left',
      styleValue: '-2px 0px 4px 0px rgba(16, 24, 40, 0.05)',
      hideStyleValueInUI: true,
      ...(isNative() && {
        styleObject: { boxShadow: '-2px 0px 4px 0px rgba(16, 24, 40, 0.05)' },
      }),
    },
    {
      id: 'shadow-overflow-right',
      name: 'overflow-right',
      styleValue: '2px 0px 4px 0px rgba(16, 24, 40, 0.05)',
      hideStyleValueInUI: true,
      ...(isNative() && {
        styleObject: { boxShadow: '2px 0px 4px 0px rgba(16, 24, 40, 0.05)' },
      }),
    },
    {
      id: 'shadow-overflow-bottom',
      name: 'overflow-bottom',
      styleValue: '0px 2px 4px 0px rgba(16, 24, 40, 0.05)',
      hideStyleValueInUI: true,
      ...(isNative() && {
        styleObject: { boxShadow: '0px 2px 4px 0px rgba(16, 24, 40, 0.05)' },
      }),
    },
    {
      id: 'shadow-overflow-top',
      name: 'overflow-top',
      styleValue: '0px -2px 4px 0px rgba(16, 24, 40, 0.05)',
      hideStyleValueInUI: true,
      ...(isNative() && {
        styleObject: { boxShadow: '0px -2px 4px 0px rgba(16, 24, 40, 0.05)' },
      }),
    },
  ],
});

/**
 * Returns border width options for all directions
 */
const getBorderWidthOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'border-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'border', name: 'sm', styleValue: '1px' },
    { id: 'border-2', name: 'md', styleValue: '2px' },
    { id: 'border-[3px]', name: 'lg', styleValue: '3px' },
    { id: 'border-4', name: 'xl', styleValue: '4px' },
  ],
  x: [
    { id: 'border-x-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'border-x', name: 'sm', styleValue: '1px' },
    { id: 'border-x-2', name: 'md', styleValue: '2px' },
    { id: 'border-x-[3px]', name: 'lg', styleValue: '3px' },
    { id: 'border-x-4', name: 'xl', styleValue: '4px' },
  ],
  y: [
    { id: 'border-y-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'border-y', name: 'sm', styleValue: '1px' },
    { id: 'border-y-2', name: 'md', styleValue: '2px' },
    { id: 'border-y-[3px]', name: 'lg', styleValue: '3px' },
    { id: 'border-y-4', name: 'xl', styleValue: '4px' },
  ],
  t: [
    { id: 'border-t-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'border-t', name: 'sm', styleValue: '1px' },
    { id: 'border-t-2', name: 'md', styleValue: '2px' },
    { id: 'border-t-[3px]', name: 'lg', styleValue: '3px' },
    { id: 'border-t-4', name: 'xl', styleValue: '4px' },
  ],
  r: [
    { id: 'border-e-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'border-e', name: 'sm', styleValue: '1px' },
    { id: 'border-e-2', name: 'md', styleValue: '2px' },
    { id: 'border-e-[3px]', name: 'lg', styleValue: '3px' },
    { id: 'border-e-4', name: 'xl', styleValue: '4px' },
  ],
  b: [
    { id: 'border-b-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'border-b', name: 'sm', styleValue: '1px' },
    { id: 'border-b-2', name: 'md', styleValue: '2px' },
    { id: 'border-b-[3px]', name: 'lg', styleValue: '3px' },
    { id: 'border-b-4', name: 'xl', styleValue: '4px' },
  ],
  l: [
    { id: 'border-s-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'border-s', name: 'sm', styleValue: '1px' },
    { id: 'border-s-2', name: 'md', styleValue: '2px' },
    { id: 'border-s-[3px]', name: 'lg', styleValue: '3px' },
    { id: 'border-s-4', name: 'xl', styleValue: '4px' },
  ],
});

/**
 * Returns border style options (none, solid, dashed, dotted)
 */
const getBorderStyleOptions = (i18nextT: TFunction) => ({
  // styleValue is the CSS equivalent of the Tailwind id — consumed by the
  // llm-tools CSS plugin for customCSS ↔ appearance.styles round-tripping.
  // hideStyleValueInUI keeps the dropdown label unchanged.
  all: [
    {
      id: 'border-none',
      name: i18nextT('interfaces:Styles.None'),
      styleValue: 'none',
      hideStyleValueInUI: true,
    },
    {
      id: 'border-solid',
      name: i18nextT('interfaces:Styles.BorderStyle.Solid'),
      styleValue: 'solid',
      hideStyleValueInUI: true,
    },
    {
      id: 'border-dashed',
      name: i18nextT('interfaces:Styles.BorderStyle.Dashed'),
      styleValue: 'dashed',
      hideStyleValueInUI: true,
    },
    {
      id: 'border-dotted',
      name: i18nextT('interfaces:Styles.BorderStyle.Dotted'),
      styleValue: 'dotted',
      hideStyleValueInUI: true,
    },
  ],
});

/**
 * Returns border radius options for all corners
 */
const getBorderRadiusOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'rounded-none', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'rounded-xxs', name: 'xxs', styleValue: '2px' },
    { id: 'rounded-xs', name: 'xs', styleValue: '4px' },
    { id: 'rounded-sm', name: 'sm', styleValue: '6px' },
    { id: 'rounded-md', name: 'md', styleValue: '8px' },
    { id: 'rounded-3xl', name: 'lg', styleValue: '10px' },
    { id: 'rounded-5xl', name: 'xl', styleValue: '12px' },
    { id: 'rounded-7xl', name: '2xl', styleValue: '16px' },
    { id: 'rounded-9xl', name: '3xl', styleValue: '20px' },
    { id: 'rounded-10xl', name: '4xl', styleValue: '24px' },
    { id: 'rounded-full', name: 'full', styleValue: '9999px' },
  ],
  tl: [
    { id: 'rounded-ts-none', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'rounded-ts-xxs', name: 'xxs', styleValue: '2px' },
    { id: 'rounded-ts-xs', name: 'xs', styleValue: '4px' },
    { id: 'rounded-ts-sm', name: 'sm', styleValue: '6px' },
    { id: 'rounded-ts-md', name: 'md', styleValue: '8px' },
    { id: 'rounded-ts-3xl', name: 'lg', styleValue: '10px' },
    { id: 'rounded-ts-5xl', name: 'xl', styleValue: '12px' },
    { id: 'rounded-ts-7xl', name: '2xl', styleValue: '16px' },
    { id: 'rounded-ts-9xl', name: '3xl', styleValue: '20px' },
    { id: 'rounded-ts-10xl', name: '4xl', styleValue: '24px' },
    { id: 'rounded-ts-full', name: 'full', styleValue: '9999px' },
  ],
  tr: [
    { id: 'rounded-te-none', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'rounded-te-xxs', name: 'xxs', styleValue: '2px' },
    { id: 'rounded-te-xs', name: 'xs', styleValue: '4px' },
    { id: 'rounded-te-sm', name: 'sm', styleValue: '6px' },
    { id: 'rounded-te-md', name: 'md', styleValue: '8px' },
    { id: 'rounded-te-3xl', name: 'lg', styleValue: '10px' },
    { id: 'rounded-te-5xl', name: 'xl', styleValue: '12px' },
    { id: 'rounded-te-7xl', name: '2xl', styleValue: '16px' },
    { id: 'rounded-te-9xl', name: '3xl', styleValue: '20px' },
    { id: 'rounded-te-10xl', name: '4xl', styleValue: '24px' },
    { id: 'rounded-te-full', name: 'full', styleValue: '9999px' },
  ],
  bl: [
    { id: 'rounded-bs-none', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'rounded-bs-xxs', name: 'xxs', styleValue: '2px' },
    { id: 'rounded-bs-xs', name: 'xs', styleValue: '4px' },
    { id: 'rounded-bs-sm', name: 'sm', styleValue: '6px' },
    { id: 'rounded-bs-md', name: 'md', styleValue: '8px' },
    { id: 'rounded-bs-3xl', name: 'lg', styleValue: '10px' },
    { id: 'rounded-bs-5xl', name: 'xl', styleValue: '12px' },
    { id: 'rounded-bs-7xl', name: '2xl', styleValue: '16px' },
    { id: 'rounded-bs-9xl', name: '3xl', styleValue: '20px' },
    { id: 'rounded-bs-10xl', name: '4xl', styleValue: '24px' },
    { id: 'rounded-bs-full', name: 'full', styleValue: '9999px' },
  ],
  br: [
    { id: 'rounded-be-none', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'rounded-be-xxs', name: 'xxs', styleValue: '2px' },
    { id: 'rounded-be-xs', name: 'xs', styleValue: '4px' },
    { id: 'rounded-be-sm', name: 'sm', styleValue: '6px' },
    { id: 'rounded-be-md', name: 'md', styleValue: '8px' },
    { id: 'rounded-be-3xl', name: 'lg', styleValue: '10px' },
    { id: 'rounded-be-5xl', name: 'xl', styleValue: '12px' },
    { id: 'rounded-be-7xl', name: '2xl', styleValue: '16px' },
    { id: 'rounded-be-9xl', name: '3xl', styleValue: '20px' },
    { id: 'rounded-be-10xl', name: '4xl', styleValue: '24px' },
    { id: 'rounded-be-full', name: 'full', styleValue: '9999px' },
  ],
});

/**
 * Returns overflow options
 * styleValue is hidden in the UI — only used by the customCSS ↔ appearance.styles bridge.
 */
const getOverflowOptions = (i18nextT: TFunction, isMobile: boolean) => ({
  all: [
    {
      id: 'overflow-visible',
      name: i18nextT('interfaces:Styles.Overflow.Visible'),
      styleValue: 'visible',
      hideStyleValueInUI: true,
    },
    {
      id: 'overflow-hidden',
      name: i18nextT('interfaces:Styles.Overflow.Hidden'),
      styleValue: 'hidden',
      hideStyleValueInUI: true,
    },
    ...(isMobile
      ? []
      : [
          {
            id: 'overflow-scroll',
            name: i18nextT('interfaces:Styles.Overflow.Scroll'),
            styleValue: 'scroll',
            hideStyleValueInUI: true,
          },
          {
            id: 'overflow-auto',
            name: i18nextT('interfaces:Styles.Overflow.Auto'),
            styleValue: 'auto',
            hideStyleValueInUI: true,
          },
          {
            id: 'overflow-clip',
            name: i18nextT('interfaces:Styles.Overflow.Clip'),
            styleValue: 'clip',
            hideStyleValueInUI: true,
          },
        ]),
  ],
  x: [
    {
      id: 'overflow-x-hidden',
      name: i18nextT('interfaces:Styles.Overflow.Hidden'),
      styleValue: 'hidden',
      hideStyleValueInUI: true,
    },
    {
      id: 'overflow-x-scroll',
      name: i18nextT('interfaces:Styles.Overflow.Scroll'),
      styleValue: 'scroll',
      hideStyleValueInUI: true,
    },
    ...(isMobile
      ? []
      : [
          {
            id: 'overflow-x-visible',
            name: i18nextT('interfaces:Styles.Overflow.Visible'),
            styleValue: 'visible',
            hideStyleValueInUI: true,
          },
          {
            id: 'overflow-x-auto',
            name: i18nextT('interfaces:Styles.Overflow.Auto'),
            styleValue: 'auto',
            hideStyleValueInUI: true,
          },
          {
            id: 'overflow-x-clip',
            name: i18nextT('interfaces:Styles.Overflow.Clip'),
            styleValue: 'clip',
            hideStyleValueInUI: true,
          },
        ]),
  ],
  y: [
    {
      id: 'overflow-y-hidden',
      name: i18nextT('interfaces:Styles.Overflow.Hidden'),
      styleValue: 'hidden',
      hideStyleValueInUI: true,
    },
    {
      id: 'overflow-y-scroll',
      name: i18nextT('interfaces:Styles.Overflow.Scroll'),
      styleValue: 'scroll',
      hideStyleValueInUI: true,
    },
    ...(isMobile
      ? []
      : [
          {
            id: 'overflow-y-visible',
            name: i18nextT('interfaces:Styles.Overflow.Visible'),
            styleValue: 'visible',
            hideStyleValueInUI: true,
          },
          {
            id: 'overflow-y-auto',
            name: i18nextT('interfaces:Styles.Overflow.Auto'),
            styleValue: 'auto',
            hideStyleValueInUI: true,
          },
          {
            id: 'overflow-y-clip',
            name: i18nextT('interfaces:Styles.Overflow.Clip'),
            styleValue: 'clip',
            hideStyleValueInUI: true,
          },
        ]),
  ],
});

/**
 * Returns min-width options
 */
const getMinWidthOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'none', name: i18nextT('interfaces:Styles.None') },
    { id: 'min-w-[200px]', name: 'xxs', styleValue: '200px' },
    { id: 'min-w-[360px]', name: 'xs', styleValue: '360px' },
    { id: 'min-w-[400px]', name: 'sm', styleValue: '400px' },
    { id: 'min-w-[540px]', name: 'md', styleValue: '540px' },
    { id: 'min-w-[640px]', name: 'lg', styleValue: '640px' },
    { id: 'min-w-[1024px]', name: 'xl', styleValue: '1024px' },
    { id: 'min-w-full', name: 'fill', styleValue: '100%' },
  ],
});

/**
 * Returns min-height options
 */
const getMinHeightOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'none', name: i18nextT('interfaces:Styles.None') },
    { id: 'min-h-[200px]', name: 'xxs', styleValue: '200px' },
    { id: 'min-h-[360px]', name: 'xs', styleValue: '360px' },
    { id: 'min-h-[400px]', name: 'sm', styleValue: '400px' },
    { id: 'min-h-[540px]', name: 'md', styleValue: '540px' },
    { id: 'min-h-[640px]', name: 'lg', styleValue: '640px' },
    { id: 'min-h-[1024px]', name: 'xl', styleValue: '1024px' },
    { id: 'min-h-full', name: 'fill', styleValue: '100%' },
  ],
});

/**
 * Returns max-width options
 */
const getMaxWidthOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'none', name: i18nextT('interfaces:Styles.None') },
    { id: 'max-w-[200px]', name: 'xxs', styleValue: '200px' },
    { id: 'max-w-[360px]', name: 'xs', styleValue: '360px' },
    { id: 'max-w-[400px]', name: 'sm', styleValue: '400px' },
    { id: 'max-w-[540px]', name: 'md', styleValue: '540px' },
    { id: 'max-w-[640px]', name: 'lg', styleValue: '640px' },
    { id: 'max-w-[1024px]', name: 'xl', styleValue: '1024px' },
    { id: 'max-w-full', name: 'fill', styleValue: '100%' },
  ],
});

/**
 * Returns max-height options
 */
const getMaxHeightOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'none', name: i18nextT('interfaces:Styles.None') },
    { id: 'max-h-[200px]', name: 'xxs', styleValue: '200px' },
    { id: 'max-h-[360px]', name: 'xs', styleValue: '360px' },
    { id: 'max-h-[400px]', name: 'sm', styleValue: '400px' },
    { id: 'max-h-[540px]', name: 'md', styleValue: '540px' },
    { id: 'max-h-[640px]', name: 'lg', styleValue: '640px' },
    { id: 'max-h-[1024px]', name: 'xl', styleValue: '1024px' },
    { id: 'max-h-full', name: 'fill', styleValue: '100%' },
  ],
});

/**
 * Returns width options
 */
const getWidthOptions = (i18nextT: TFunction, isMobile: boolean) => ({
  all: [
    { id: 'w-auto', name: i18nextT('interfaces:Styles.Auto'), styleValue: 'Auto' },
    { id: 'w-[200px]', name: 'xxs', styleValue: '200px' },
    { id: 'w-[360px]', name: 'xs', styleValue: '360px' },
    { id: 'w-[400px]', name: 'sm', styleValue: '400px' },
    { id: 'w-[540px]', name: 'md', styleValue: '540px' },
    { id: 'w-[640px]', name: 'lg', styleValue: '640px' },
    { id: 'w-[1024px]', name: 'xl', styleValue: '1024px' },
    {
      id: 'w-full',
      name: i18nextT('interfaces:Styles.Width.Fill'),
      styleValue: '100%',
    },
    ...(isMobile
      ? []
      : [
          {
            id: 'inherit',
            name: i18nextT('interfaces:Styles.Width.Inherit'),
            styleValue: 'inherit',
            hideStyleValueInUI: true,
          },
          {
            id: 'w-fit',
            name: i18nextT('interfaces:Styles.Width.FitContent'),
            styleValue: 'fit-content',
            hideStyleValueInUI: true,
          },
        ]),
  ],
});

/**
 * Returns divider width options
 */
const getDividerWidthOptions = () => ({
  all: [
    { id: 'w-[1px]', name: 'xxs', styleValue: '1px' },
    { id: 'w-[2px]', name: 'xs', styleValue: '2px' },
    { id: 'w-[3px]', name: 'sm', styleValue: '3px' },
    { id: 'w-[4px]', name: 'md', styleValue: '4px' },
  ],
});

/**
 * Returns divider height options
 */
const getDividerHeightOptions = () => ({
  all: [
    { id: 'h-[1px]', name: 'xxs', styleValue: '1px' },
    { id: 'h-[2px]', name: 'xs', styleValue: '2px' },
    { id: 'h-[3px]', name: 'sm', styleValue: '3px' },
    { id: 'h-[4px]', name: 'md', styleValue: '4px' },
  ],
});

/**
 * Returns height options
 */
const getHeightOptions = (i18nextT: TFunction, isMobile: boolean) => ({
  all: [
    { id: 'h-auto', name: i18nextT('interfaces:Styles.Auto'), styleValue: 'Auto' },
    { id: 'h-[200px]', name: 'xxs', styleValue: '200px' },
    { id: 'h-[360px]', name: 'xs', styleValue: '360px' },
    { id: 'h-[400px]', name: 'sm', styleValue: '400px' },
    { id: 'h-[540px]', name: 'md', styleValue: '540px' },
    { id: 'h-[640px]', name: 'lg', styleValue: '640px' },
    { id: 'h-[1024px]', name: 'xl', styleValue: '1024px' },
    {
      id: 'h-full',
      name: i18nextT('interfaces:Styles.Height.Fill'),
      styleValue: '100%',
    },
    ...(isMobile
      ? []
      : [
          {
            id: 'h-fit',
            name: i18nextT('interfaces:Styles.Height.Fit'),
            styleValue: 'fit-content',
            hideStyleValueInUI: true,
          },
        ]),
  ],
});

/**
 * Returns flex wrap options
 */
const getFlexWrapOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'flex-nowrap', name: i18nextT('interfaces:Styles.FlexWrap.NoWrap') },
    { id: 'flex-wrap', name: i18nextT('interfaces:Styles.FlexWrap.Wrap') },
    { id: 'flex-wrap-reverse', name: i18nextT('interfaces:Styles.FlexWrap.WrapReverse') },
  ],
});

/**
 * Returns flex direction options (row, column, row-reverse, column-reverse)
 */
const getFlexDirectionOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'flex-row', name: i18nextT('interfaces:Styles.FlexDirection.Row') },
    { id: 'flex-row-reverse', name: i18nextT('interfaces:Styles.FlexDirection.RowReverse') },
    { id: 'flex-col', name: i18nextT('interfaces:Styles.FlexDirection.Column') },
    { id: 'flex-col-reverse', name: i18nextT('interfaces:Styles.FlexDirection.ColumnReverse') },
  ],
});

/**
 * Returns justify-content options (start, center, end, space-between, space-around, space-evenly)
 */
const getJustifyContentOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'justify-start', name: i18nextT('interfaces:Styles.JustifyContent.Start') },
    { id: 'justify-center', name: i18nextT('interfaces:Styles.JustifyContent.Center') },
    { id: 'justify-end', name: i18nextT('interfaces:Styles.JustifyContent.End') },
    { id: 'justify-between', name: i18nextT('interfaces:Styles.JustifyContent.Between') },
    { id: 'justify-around', name: i18nextT('interfaces:Styles.JustifyContent.Around') },
    { id: 'justify-evenly', name: i18nextT('interfaces:Styles.JustifyContent.Evenly') },
  ],
});

/**
 * Returns align-items options (start, center, end, stretch, baseline)
 */
const getAlignItemsOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'items-start', name: i18nextT('interfaces:Styles.AlignItems.Start') },
    { id: 'items-center', name: i18nextT('interfaces:Styles.AlignItems.Center') },
    { id: 'items-end', name: i18nextT('interfaces:Styles.AlignItems.End') },
    { id: 'items-stretch', name: i18nextT('interfaces:Styles.AlignItems.Stretch') },
    { id: 'items-baseline', name: i18nextT('interfaces:Styles.AlignItems.Baseline') },
  ],
});

/**
 * Returns layout options (flex-shrink, flex-grow, self-alignment)
 */
const getLayoutOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'none', name: i18nextT('interfaces:Styles.TextDecoration.None') },
    { id: 'flex-shrink-0', name: i18nextT('interfaces:Styles.Layout.Hug') },
    { id: 'flex-shrink', name: i18nextT('interfaces:Styles.Layout.Contain') },
    { id: 'flex-grow', name: i18nextT('interfaces:Styles.Layout.Fill') },
    { id: 'self-start', name: i18nextT('interfaces:Styles.Layout.SelfStart') },
    { id: 'self-center', name: i18nextT('interfaces:Styles.Layout.SelfCenter') },
    { id: 'self-end', name: i18nextT('interfaces:Styles.Layout.SelfEnd') },
    { id: 'self-stretch', name: i18nextT('interfaces:Styles.Layout.SelfStretch') },
    { id: 'self-baseline', name: i18nextT('interfaces:Styles.Layout.SelfBaseline') },
    { id: 'self-auto', name: i18nextT('interfaces:Styles.Layout.SelfAuto') },
  ],
});

/**
 * Returns text decoration options
 */
const getTextDecorationOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'none', name: i18nextT('interfaces:Styles.TextDecoration.None') },
    { id: 'underline', name: i18nextT('interfaces:Styles.TextDecoration.Underline') },
    { id: 'overline', name: i18nextT('interfaces:Styles.TextDecoration.Overline') },
    { id: 'line-through', name: i18nextT('interfaces:Styles.TextDecoration.LineThrough') },
    { id: 'no-underline', name: i18nextT('interfaces:Styles.TextDecoration.NoUnderline') },
  ],
});

/**
 * Returns font family options
 */
const getFontFamilyOptions = (i18nextT: TFunction) => ({
  all: [
    {
      id: 'font-[family-name:var(--font-inter)]',
      name: i18nextT('interfaces:Styles.FontFamily.Inter'),
    },
    { id: 'font-mono', name: i18nextT('interfaces:Styles.FontFamily.Mono') },
    {
      id: 'font-[family-name:var(--font-source-serif-4)]',
      name: i18nextT('interfaces:Styles.FontFamily.SourceSerif4'),
    },
  ],
});

/**
 * Returns background size options
 */
const getBackgroundSizeOptions = (i18nextT: TFunction) => ({
  // styleValue is the CSS equivalent of the Tailwind id — consumed by the
  // llm-tools CSS plugin for customCSS ↔ appearance.styles round-tripping.
  // hideStyleValueInUI keeps the dropdown label unchanged.
  all: [
    {
      id: 'bg-auto',
      name: i18nextT('interfaces:Styles.BackgroundSize.Auto'),
      styleValue: 'auto',
      hideStyleValueInUI: true,
    },
    {
      id: 'bg-cover',
      name: i18nextT('interfaces:Styles.BackgroundSize.Cover'),
      styleValue: 'cover',
      hideStyleValueInUI: true,
    },
    {
      id: 'bg-contain',
      name: i18nextT('interfaces:Styles.BackgroundSize.Contain'),
      styleValue: 'contain',
      hideStyleValueInUI: true,
    },
    {
      id: 'bg-[length:100%_100%]',
      name: i18nextT('interfaces:Styles.BackgroundSize.Fit'),
      styleValue: '100% 100%',
      hideStyleValueInUI: true,
    },
  ],
});

/**
 * Returns background repeat options
 */
const getBackgroundRepeatOptions = (i18nextT: TFunction) => ({
  all: [
    {
      id: 'bg-repeat',
      name: i18nextT('interfaces:Styles.BackgroundRepeat.Repeat'),
      styleValue: 'repeat',
      hideStyleValueInUI: true,
    },
    {
      id: 'bg-no-repeat',
      name: i18nextT('interfaces:Styles.BackgroundRepeat.NoRepeat'),
      styleValue: 'no-repeat',
      hideStyleValueInUI: true,
    },
    {
      id: 'bg-repeat-x',
      name: i18nextT('interfaces:Styles.BackgroundRepeat.RepeatX'),
      styleValue: 'repeat-x',
      hideStyleValueInUI: true,
    },
    {
      id: 'bg-repeat-y',
      name: i18nextT('interfaces:Styles.BackgroundRepeat.RepeatY'),
      styleValue: 'repeat-y',
      hideStyleValueInUI: true,
    },
  ],
});

/**
 * Returns visibility options
 * styleValue is hidden in the UI — only used by the customCSS ↔ appearance.styles bridge.
 */
const getVisibilityOptions = (i18nextT: TFunction) => ({
  all: [
    {
      id: 'visible',
      name: i18nextT('interfaces:Styles.Visibility.Visible'),
      styleValue: 'visible',
      hideStyleValueInUI: true,
    },
    {
      id: 'invisible',
      name: i18nextT('interfaces:Styles.Visibility.Invisible'),
      styleValue: 'hidden',
      hideStyleValueInUI: true,
    },
  ],
});

/**
 * Returns display options (Tailwind class ids resolve to display CSS in theme-manager).
 * First option is default (visible); use "hidden" for display: none so the element is removed from layout.
 * styleValue is hidden in the UI — only used by the customCSS ↔ appearance.styles bridge.
 */
const getDisplayOptions = (i18nextT: TFunction) => ({
  all: [
    {
      id: 'block',
      name: i18nextT('interfaces:Styles.Display.Block'),
      styleValue: 'block',
      hideStyleValueInUI: true,
    },
    {
      id: 'hidden',
      name: i18nextT('interfaces:Styles.Display.None'),
      styleValue: 'none',
      hideStyleValueInUI: true,
    },
    {
      id: 'flex',
      name: i18nextT('interfaces:Styles.Display.Flex'),
      styleValue: 'flex',
      hideStyleValueInUI: true,
    },
    {
      id: 'inline',
      name: i18nextT('interfaces:Styles.Display.Inline'),
      styleValue: 'inline',
      hideStyleValueInUI: true,
    },
    {
      id: 'inline-block',
      name: i18nextT('interfaces:Styles.Display.InlineBlock'),
      styleValue: 'inline-block',
      hideStyleValueInUI: true,
    },
  ],
});

/**
 * Returns position options
 * styleValue is hidden in the UI — only used by the customCSS ↔ appearance.styles bridge.
 */
const getPositionOptions = (i18nextT: TFunction, isMobile: boolean) => ({
  all: [
    {
      id: 'static',
      name: i18nextT('interfaces:Styles.Position.Static'),
      styleValue: 'static',
      hideStyleValueInUI: true,
    },
    ...(isMobile
      ? []
      : [
          {
            id: 'fixed',
            name: i18nextT('interfaces:Styles.Position.Fixed'),
            styleValue: 'fixed',
            hideStyleValueInUI: true,
          },
        ]),
    {
      id: 'absolute',
      name: i18nextT('interfaces:Styles.Position.Absolute'),
      styleValue: 'absolute',
      hideStyleValueInUI: true,
    },
    {
      id: 'relative',
      name: i18nextT('interfaces:Styles.Position.Relative'),
      styleValue: 'relative',
      hideStyleValueInUI: true,
    },
    ...(isMobile
      ? []
      : [
          {
            id: 'sticky',
            name: i18nextT('interfaces:Styles.Position.Sticky'),
            styleValue: 'sticky',
            hideStyleValueInUI: true,
          },
        ]),
  ],
});

/**
 * Returns top/left inset options for positioned elements (e.g. close button).
 * Uses direct CSS values so theme-manager can output top/left without Tailwind class resolution.
 */
const getInsetTopOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'none', name: i18nextT('interfaces:Styles.None') },
    { id: '0px', name: '0px', styleValue: '0px' },
    { id: '4px', name: '4px', styleValue: '4px' },
    { id: '8px', name: '8px', styleValue: '8px' },
    { id: '12px', name: '12px', styleValue: '12px' },
    { id: '16px', name: '16px', styleValue: '16px' },
  ],
});

const getInsetLeftOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'none', name: i18nextT('interfaces:Styles.None') },
    { id: '0px', name: '0px', styleValue: '0px' },
    { id: '4px', name: '4px', styleValue: '4px' },
    { id: '8px', name: '8px', styleValue: '8px' },
    { id: '12px', name: '12px', styleValue: '12px' },
    { id: '16px', name: '16px', styleValue: '16px' },
  ],
});

/**
 * Returns axis-offset options for positioned elements (e.g. badge translate-X / translate-Y).
 * Distinct from inset top/left because these values are designed to feed into transform
 * calc() expressions — `none` and `auto` would produce invalid CSS in that context, so the
 * preset list is limited to direct length values. customStyles enables free-form px/% input.
 */
const getOffsetAxisOptions = (i18nextT: TFunction) => ({
  all: [
    { id: '0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: '4px', name: 'sm', styleValue: '4px' },
    { id: '8px', name: 'md', styleValue: '8px' },
    { id: '12px', name: 'lg', styleValue: '12px' },
    { id: '16px', name: 'xl', styleValue: '16px' },
  ],
});

/**
 * Returns break-after options for print/pagination
 * styleValue is hidden in the UI — only used by the customCSS ↔ appearance.styles bridge.
 */
const getBreakAfterOptions = (i18nextT: TFunction) => ({
  all: [
    {
      id: 'break-after-auto',
      name: i18nextT('interfaces:Styles.Break.Auto'),
      styleValue: 'auto',
      hideStyleValueInUI: true,
    },
    {
      id: 'break-after-avoid',
      name: i18nextT('interfaces:Styles.Break.Avoid'),
      styleValue: 'avoid',
      hideStyleValueInUI: true,
    },
    {
      id: 'break-after-page',
      name: i18nextT('interfaces:Styles.Break.Page'),
      styleValue: 'page',
      hideStyleValueInUI: true,
    },
    {
      id: 'break-after-column',
      name: i18nextT('interfaces:Styles.Break.Column'),
      styleValue: 'column',
      hideStyleValueInUI: true,
    },
  ],
});

/**
 * Returns opacity style configuration
 */
const getOpacityConfig = () => ({
  isDirectional: false,
  picker: 'input' as const,
  getStyleObject: (value: string) => {
    return { opacity: `${Number(value) / 100}` };
  },
  placeholder: '100',
});

/**
 * Returns border stroke configuration (combines width, style, and color)
 */
const getBorderStrokeConfig = () => ({
  isDirectional: true,
  customStyles: {
    customDirectionKeys: ['t', 'r', 'b', 'l'],
    enabled: true,
    units: [{ id: 'px', name: 'px' }],
  },
  conditionalStyles: { enabled: true },
  isTabbedColorPicker: true,
  colorGroups: borderColorGroups.map(({ id, label, colors }) => ({ id, label, colors })),
});

/**
 * Returns border color configuration
 */
const getBorderColorConfig = () => ({
  picker: 'color' as const,
  conditionalStyles: { enabled: true },
  isTabbedColorPicker: true,
  isDirectional: false,
  customStyles: {
    customDirectionKeys: ['t', 'r', 'b', 'l'],
    enabled: true,
  },
  colorGroups: borderColorGroups.map(({ id, label, colors }) => ({ id, label, colors })),
  getStyleObject: (value: string) => ({ borderColor: value }),
});

/**
 * Returns outline width options. outline is non-directional (single ring around element),
 * so we only expose the `all` axis. values mirror the border width scale.
 */
const getOutlineWidthOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'outline-w-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'outline-w-sm', name: 'sm', styleValue: '1px' },
    { id: 'outline-w-md', name: 'md', styleValue: '2px' },
    { id: 'outline-w-lg', name: 'lg', styleValue: '3px' },
    { id: 'outline-w-xl', name: 'xl', styleValue: '4px' },
  ],
});

/**
 * Returns outline style options (none, solid, dashed, dotted, double).
 * uses non-Tailwind ids so CSSOM lookup is skipped and styleValue is used directly.
 */
const getOutlineStyleOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'outline-s-none', name: i18nextT('interfaces:Styles.None'), styleValue: 'none' },
    {
      id: 'outline-s-solid',
      name: i18nextT('interfaces:Styles.BorderStyle.Solid'),
      styleValue: 'solid',
    },
    {
      id: 'outline-s-dashed',
      name: i18nextT('interfaces:Styles.BorderStyle.Dashed'),
      styleValue: 'dashed',
    },
    {
      id: 'outline-s-dotted',
      name: i18nextT('interfaces:Styles.BorderStyle.Dotted'),
      styleValue: 'dotted',
    },
    { id: 'outline-s-double', name: 'Double', styleValue: 'double' },
  ],
});

/**
 * Returns outline color configuration. uses outlineColorGroups (derived from
 * borderColorGroups with `outline-*` prefixed ids) so the picker ids never collide
 * with border-color Tailwind classes — theme-manager falls through to the configured
 * value cache and emits the value under `outline-color` cleanly.
 */
const getOutlineColorConfig = () => ({
  picker: 'color' as const,
  conditionalStyles: { enabled: true },
  isTabbedColorPicker: true,
  isDirectional: false,
  colorGroups: outlineColorGroups.map(({ id, label, colors }) => ({ id, label, colors })),
  getStyleObject: (value: string) => ({ outlineColor: value }),
});

/**
 * Returns outline offset options. controls the gap between element edge and outline ring.
 */
const getOutlineOffsetOptions = (i18nextT: TFunction) => ({
  all: [
    { id: 'outline-o-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
    { id: 'outline-o-sm', name: 'sm', styleValue: '1px' },
    { id: 'outline-o-md', name: 'md', styleValue: '2px' },
    { id: 'outline-o-lg', name: 'lg', styleValue: '4px' },
  ],
});

/**
 * Returns background color configuration
 */
const getBackgroundColorConfig = () => ({
  picker: 'color' as const,
  conditionalStyles: { enabled: true },
  isDirectional: false,
  isTabbedColorPicker: true,
  colorGroups: backgroundColorGroups.map(({ id, label, colors }) => ({ id, label, colors })),
  getStyleObject: (value: string) => ({ backgroundColor: value }),
});

/**
 * Returns foreground color configuration (CSS `color`). Used e.g. for icon SVG color.
 */
const getColorConfig = () => ({
  picker: 'color' as const,
  conditionalStyles: { enabled: true },
  isDirectional: false,
  isTabbedColorPicker: true,
  colorGroups: backgroundColorGroups.map(({ id, label, colors }) => ({ id, label, colors })),
  getStyleObject: (value: string) => ({ color: value }),
});

/**
 * Returns chart hover color configuration
 */
const getChartHoverColorConfig = () => ({
  picker: 'color' as const,
  conditionalStyles: { enabled: false },
  isDirectional: false,
  colorGroups: chartColorGroups.map(({ id, label, colors }) => ({ id, label, colors })),
});

/**
 * Returns chart active color configuration
 */
const getChartActiveColorConfig = () => ({
  picker: 'color' as const,
  conditionalStyles: { enabled: false },
  isDirectional: false,
  colorGroups: chartColorGroups.map(({ id, label, colors }) => ({ id, label, colors })),
});

/**
 * Returns rotation style configuration
 */
const getRotationConfig = () => ({
  isDirectional: false,
  picker: 'input' as const,
  getStyleObject: (value: string) => ({ transform: `rotate(${value})` }),
  placeholder: '0',
});

/**
 * Returns line-height options. custom values support arbitrary CSS via the unit selector.
 */
const getLineHeightOptions = () => ({
  all: [
    { id: '1rem', name: '1rem', styleValue: '16px' },
    { id: '1.25rem', name: '1.25rem', styleValue: '20px' },
    { id: '1.5rem', name: '1.5rem', styleValue: '24px' },
    { id: '1.625rem', name: '1.625rem', styleValue: '26px' },
    { id: '1.75rem', name: '1.75rem', styleValue: '28px' },
    { id: '2rem', name: '2rem', styleValue: '32px' },
    { id: '2.25rem', name: '2.25rem', styleValue: '36px' },
    { id: '2.5rem', name: '2.5rem', styleValue: '40px' },
    { id: '2.75rem', name: '2.75rem', styleValue: '44px' },
    { id: '3.25rem', name: '3.25rem', styleValue: '52px' },
    { id: '4rem', name: '4rem', styleValue: '64px' },
  ],
});

/**
 * Returns letter-spacing options. custom values support arbitrary CSS via the unit selector.
 */
const getLetterSpacingOptions = () => ({
  all: [
    { id: '-0.015625rem', name: '-0.015625rem', styleValue: '-0.25px' },
    { id: '0rem', name: '0rem', styleValue: '0px' },
    { id: '0.00625rem', name: '0.00625rem', styleValue: '0.1px' },
    { id: '0.009375rem', name: '0.009375rem', styleValue: '0.15px' },
    { id: '0.015625rem', name: '0.015625rem', styleValue: '0.25px' },
    { id: '0.025rem', name: '0.025rem', styleValue: '0.4px' },
    { id: '0.03125rem', name: '0.03125rem', styleValue: '0.5px' },
  ],
});

/**
 * Returns background image configuration
 */
const getBackgroundImageConfig = () => ({
  isDirectional: false,
  picker: 'imagePicker' as const,
  // wraps raw URLs in url() for valid CSS; handles dynamically resolved
  // variable values that bypass the adapter's url() wrapping
  getStyleObject: (value: string) => ({
    backgroundImage: value.startsWith('url(') || !isUrl(value) ? value : `url("${value}")`,
  }),
});

// ============================================================================
// MAIN STYLE CONFIG FUNCTION
// ============================================================================

//todo: remove all - and hard coded options when custom input is developed
export const getStyleItemConfig = (
  i18nextT: TFunction,
  applicationType: ApplicationType | undefined,
  isBuilderContext?: boolean,
) => {
  const isMobile = applicationType === ApplicationType.MOBILE;
  return {
    padding: getPaddingConfig(i18nextT, applicationType),
    margin: getMarginConfig(i18nextT, applicationType),
    legendsPadding: getPaddingConfig(i18nextT, applicationType),
    tableCellPadding: getTableCellPaddingConfig(i18nextT),
    gap: {
      isDirectional: true,
      customStyles: { customDirectionKeys: ['x', 'y'], enabled: true },
      options: getGapOptions(i18nextT),
    },
    // TODO: Remove this
    gapSecondary: {
      isDirectional: true,
      customStyles: {
        customDirectionKeys: ['x', 'y'],
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      options: getGapOptions(i18nextT),
    },
    boxShadow: {
      isDirectional: false,
      // we want to allow entering custom values in the theme builder
      customStyles: { enabled: Boolean(!isBuilderContext) },
      options: getBoxShadowOptions(i18nextT),
    },
    borderWidth: {
      isDirectional: true,
      customStyles: {
        customDirectionKeys: ['t', 'r', 'b', 'l'],
        enabled: true,
      },
      options: getBorderWidthOptions(i18nextT),
    },
    borderStyle: {
      isDirectional: false,
      customStyles: {
        customDirectionKeys: ['t', 'r', 'b', 'l'],
        enabled: true,
      },
      options: getBorderStyleOptions(i18nextT),
    },
    borderStroke: getBorderStrokeConfig(),
    borderRadius: {
      isDirectional: true,
      customStyles: {
        customDirectionKeys: ['tl', 'tr', 'bl', 'br'],
        enabled: true,
      },
      options: getBorderRadiusOptions(i18nextT),
    },
    borderColor: getBorderColorConfig(),
    outlineWidth: {
      isDirectional: false,
      customStyles: { enabled: true, units: [{ id: 'px', name: 'px' }] },
      options: getOutlineWidthOptions(i18nextT),
    },
    outlineStyle: {
      isDirectional: false,
      options: getOutlineStyleOptions(i18nextT),
    },
    outlineColor: getOutlineColorConfig(),
    outlineOffset: {
      isDirectional: false,
      customStyles: { enabled: true, units: [{ id: 'px', name: 'px' }] },
      options: getOutlineOffsetOptions(i18nextT),
    },
    backgroundColor: getBackgroundColorConfig(),
    color: getColorConfig(),
    chartHoverColor: getChartHoverColorConfig(),
    chartActiveColor: getChartActiveColorConfig(),
    rotation: getRotationConfig(),
    overflow: {
      isDirectional: true,
      options: getOverflowOptions(i18nextT, isMobile),
    },
    minWidth: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: getMinWidthOptions(i18nextT),
    },
    minHeight: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: getMinHeightOptions(i18nextT),
    },
    maxWidth: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: getMaxWidthOptions(i18nextT),
    },
    maxHeight: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      allowCustom: true,
      isDirectional: false,
      options: getMaxHeightOptions(i18nextT),
    },
    width: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: getWidthOptions(i18nextT, isMobile),
    },
    dividerWidth: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: getDividerWidthOptions(),
    },
    dividerHeight: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: getDividerHeightOptions(),
    },
    height: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: getHeightOptions(i18nextT, isMobile),
    },
    flexWrap: {
      isDirectional: false,
      options: getFlexWrapOptions(i18nextT),
    },
    flexDirection: {
      isDirectional: false,
      options: getFlexDirectionOptions(i18nextT),
    },
    justifyContent: {
      isDirectional: false,
      options: getJustifyContentOptions(i18nextT),
    },
    alignItems: {
      isDirectional: false,
      options: getAlignItemsOptions(i18nextT),
    },
    layout: {
      isDirectional: false,
      options: getLayoutOptions(i18nextT),
    },
    textDecoration: {
      isDirectional: false,
      options: getTextDecorationOptions(i18nextT),
    },
    fontFamily: {
      isDirectional: false,
      options: getFontFamilyOptions(i18nextT),
    },
    backgroundImage: getBackgroundImageConfig(),
    backgroundSize: {
      isDirectional: false,
      options: getBackgroundSizeOptions(i18nextT),
    },
    backgroundRepeat: {
      isDirectional: false,
      options: getBackgroundRepeatOptions(i18nextT),
    },
    visibility: {
      isDirectional: false,
      options: getVisibilityOptions(i18nextT),
    },
    display: {
      isDirectional: false,
      options: getDisplayOptions(i18nextT),
    },
    position: {
      isDirectional: false,
      options: getPositionOptions(i18nextT, isMobile),
    },
    top: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: getInsetTopOptions(i18nextT),
    },
    left: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: getInsetLeftOptions(i18nextT),
    },
    offsetX: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: getOffsetAxisOptions(i18nextT),
    },
    offsetY: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: getOffsetAxisOptions(i18nextT),
    },
    breakAfter: {
      isDirectional: false,
      options: getBreakAfterOptions(i18nextT),
    },
    lineHeight: {
      isDirectional: false,
      customStyles: {
        enabled: true,
        units: [
          { id: 'rem', name: 'rem' },
          { id: 'px', name: 'px' },
          { id: 'em', name: 'em' },
        ],
      },
      options: getLineHeightOptions(),
    },
    letterSpacing: {
      isDirectional: false,
      customStyles: {
        enabled: true,
        units: [
          { id: 'rem', name: 'rem' },
          { id: 'px', name: 'px' },
          { id: 'em', name: 'em' },
        ],
      },
      options: getLetterSpacingOptions(),
    },
    opacity: getOpacityConfig(),
  } as Record<
    string,
    {
      options?: Record<keyof AppearanceStyle, StyleSizeOption[]>;
      isDirectional: boolean;
      customStyles?: {
        customDirectionKeys?: string[];
        enabled: boolean;
        units?: { id: string; name: string }[];
      };
      conditionalStyles?: { enabled: boolean };
      colorGroups?: ColorGroups[];
      picker?: 'color' | 'input' | 'imagePicker';
      isTabbedColorPicker?: boolean;
      getStyleObject?: (value: string) => Record<string, string>;
      placeholder?: string;
    }
  >;
};
